import { useMemo } from 'react'
import { useEventData } from '@/contexts/EventDataContext'

function fmtRevenue(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toLocaleString()}`
}

function dayLabel(date: string, today: string, startDate: string): string {
  if (date === today) return 'Today'
  const todayD = new Date(today + 'T12:00:00')
  const dateD = new Date(date + 'T12:00:00')
  const diffMs = todayD.getTime() - dateD.getTime()
  const diffDays = Math.round(diffMs / 86_400_000)
  if (diffDays === 1) return 'Yesterday'

  const startD = new Date(startDate + 'T12:00:00')
  const dayNum = Math.round((dateD.getTime() - startD.getTime()) / 86_400_000) + 1
  return `Day ${dayNum}`
}

function shortDate(date: string): string {
  const d = new Date(date + 'T12:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function DaySwitcher() {
  const { eventDays, selectedDate, setSelectedDate, dayLoading } = useEventData()

  const pills = useMemo(() => {
    if (!eventDays?.days?.length) return []
    const today = eventDays.today
    const startDate = eventDays.start_date
    return eventDays.days.map(d => ({
      date: d.date,
      label: dayLabel(d.date, today, startDate),
      shortDate: shortDate(d.date),
      revenue: d.revenue,
      isToday: d.date === today,
      hasData: d.scans > 0,
    }))
  }, [eventDays])

  if (!eventDays || pills.length === 0) return null

  return (
    <div className="flex items-center gap-2">
      {/* All / Event Total pill */}
      <button
        type="button"
        onClick={() => setSelectedDate(null)}
        className={`shrink-0 flex flex-col items-center rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
          selectedDate === null
            ? 'bg-butler-600 text-white shadow-brand-tinted'
            : 'bg-slate-100 text-ink-soft hover:bg-butler-50 hover:text-butler-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
        }`}
      >
        <span>All Days</span>
      </button>

      {/* Per-day pills — horizontal scroll on mobile */}
      <div className="flex-1 overflow-x-auto scrollbar-thin">
        <div className="flex items-stretch gap-1.5 pb-1">
          {pills.map(pill => {
            const isActive = selectedDate === pill.date
            return (
              <button
                key={pill.date}
                type="button"
                onClick={() => setSelectedDate(pill.date)}
                className={`shrink-0 flex flex-col items-center rounded-lg px-3 py-2 min-w-[72px] transition-colors ${
                  isActive
                    ? 'bg-butler-600 text-white shadow-brand-tinted'
                    : pill.isToday
                      ? 'bg-butler-50 text-butler-700 ring-1 ring-butler-300 dark:bg-butler-900/30 dark:text-butler-200 dark:ring-butler-600'
                      : 'bg-slate-100 text-ink-soft hover:bg-butler-50 hover:text-butler-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
                title={`${pill.shortDate} — ${fmtRevenue(pill.revenue)}`}
              >
                <span className="text-xs font-semibold leading-tight">{pill.label}</span>
                <span className={`text-[10px] leading-tight mt-0.5 ${
                  isActive ? 'text-white/80' : 'text-ink-soft/70 dark:text-slate-500'
                }`}>
                  {pill.shortDate}
                </span>
                {pill.hasData && (
                  <span className={`text-[9px] leading-tight mt-0.5 font-medium ${
                    isActive ? 'text-white/70' : 'text-ink-soft/60 dark:text-slate-500'
                  }`}>
                    {fmtRevenue(pill.revenue)}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {dayLoading && (
        <div className="shrink-0 ml-1">
          <div className="h-3 w-3 border-2 border-butler-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}
